import React from "react";

import { Link } from "react-router-dom";

import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

import { ReactComponent as ArrowDownBlack } from "commons/assets/icons/arrow_down_black.svg";
import { ReactComponent as ArrowRight } from "commons/assets/icons/arrow_right.svg";

import SubMenuItem from "commons/components/layout/menu/SubMenuItem";

import { useMenuOpenActionsContext } from "commons/context/menu";

import useMatchLocation from "commons/hooks/useMatchLocation";

import { MenuType } from "commons/types/menu";

type PropsType = {
    menu: MenuType;
    baseUiUrl?: string;
    menuVersion?: string;
};

function MenuItem({ menu, baseUiUrl, menuVersion }: PropsType) {
    const { toggleMenuWhenClose } = useMenuOpenActionsContext();

    const match = useMatchLocation(menu.path, baseUiUrl);

    const ariaExpanded: boolean | undefined = React.useMemo(() => {
        if (menu.children.length > 0) {
            return match;
        }

        return undefined;
    }, [menu.children.length, match]);

    const ariaControls: string | undefined = React.useMemo(() => {
        if (menu.children.length > 0) {
            return `children-${menu.label.toLowerCase().replace(/\s/g, "")}`;
        }

        return undefined;
    }, [menu.children.length, menu.label]);

    return (
        <div className={`item ${match ? " active " : ""}`} data-testid={"menu-item"}>
            <div
                className={`first-level ${menu.children.length > 0 ? " with-arrow " : ""}`}
                data-testid={"first-level"}
            >
                {menu.labelElement}

                {!menu.labelElement && (
                    <>
                        {menu.children.length > 0 && menuVersion === undefined && (
                            <ArrowDownBlack
                                className={`arrow-down ${match ? " active " : ""}`}
                                data-testid={"arrow-down"}
                            />
                        )}

                        <Link
                            id={`parent-${menu.label.toLowerCase().replace(/\s/g, "")}`}
                            to={menu.path}
                            data-testid={"menu-item-link"}
                            onClick={
                                menu.children.length === 0 || menu.children.length === 1
                                    ? toggleMenuWhenClose
                                    : undefined
                            }
                            aria-current={match ? "page" : undefined}
                            aria-expanded={ariaExpanded}
                            aria-controls={ariaControls}
                        >
                            {menu.children.length > 0 && menuVersion === undefined && (
                                <ArrowRight
                                    className={`arrow-right ${!match ? " active " : ""}`}
                                    data-testid={"arrow-right"}
                                />
                            )}

                            <div className={"label-icon"}>
                                <p lang={menu.lang}>{menu.label}</p>
                                {menu.icon ?? null}
                            </div>

                            {menu.children.length > 0 && menuVersion === "v2" && (
                                <ExpandMoreIcon
                                    className={`expand-more-v2 ${!match ? " active " : ""}`}
                                    data-testid={"expand-more-v2"}
                                />
                            )}

                            {menu.children.length > 0 && menuVersion === "v2" && (
                                <ExpandLessIcon
                                    className={`expand-less-v2 ${match ? " active " : ""}`}
                                    data-testid={"expand-less-v2"}
                                />
                            )}
                        </Link>
                    </>
                )}

                {menu.notificationBadge ?? null}
            </div>

            {menu.children.length > 0 && (
                <div
                    id={`children-${menu.label.toLowerCase().replace(/\s/g, "")}`}
                    role={"menu"}
                    aria-labelledby={`parent-${menu.label.toLowerCase().replace(/\s/g, "")}`}
                    className={"submenu-container"}
                >
                    {menu.children.map((subMenu) => {
                        return (
                            <SubMenuItem
                                key={subMenu.path}
                                subMenu={subMenu}
                                menuVersion={menuVersion}
                            />
                        );
                    })}
                </div>
            )}
        </div>
    );
}

export default MenuItem;
